import React, {
    FC, useCallback, ReactElement,
} from 'react';
import { SendOutlined, LinkOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import commonStyle from '../../../../Common/Typography/Typography.module.scss';
import FileActionsDropdown, { MenuClickCallback } from '../FileActionsDropdown';
import { useMenu } from '../../../FileAction/hooks';
import ColoredTooltip from '../../../../Common/ColoredTooltip';
import { FileMenu } from '@/types/types';
import { FileActionType } from '../../../FileAction/menuItems';
import { OpenOptions } from '@/config/openOptions';
import { FilesListType } from '../../../../../stores';
import styles from './Actions.module.scss';

export interface ActionsProps {
    fileId: string;
    menu: Partial<FileMenu>;
    handleMenuClick: (action: FileActionType, fid: string) => void;
    filesListType?: FilesListType;
    openIcon?: ReactElement;
    fileSize?: number;
}

const Actions: FC<ActionsProps> = observer(({
    fileId,
    menu,
    filesListType,
    handleMenuClick,
    openIcon = null,
    fileSize = 0,
}) => {
    const { t } = useTranslation();
    const {
        openWith,
        options,
        hasShare,
        hasCopyLink,
        hasManageAccess,
    } = useMenu(menu, filesListType, fileSize);

    const moreOptions: FileActionType[] = [...options];
    const downloadIndex = moreOptions.indexOf(OpenOptions.download);
    if (hasManageAccess) {
        moreOptions.splice(downloadIndex + 1, 0, 'manage_access');
        moreOptions.unshift('new_share');
    }

    const onFileActionClick = useCallback<MenuClickCallback>((event) => {
        handleMenuClick(event.key as FileActionType, fileId);
    }, [fileId]);

    return (
        <div className={styles['files-action']} data-clickable onClick={(e) => e.stopPropagation()}>
            {openWith.length > 0
            && (
                <FileActionsDropdown
                    buttonClass={styles['open-grid-area']}
                    buttonTitle="open"
                    options={openWith}
                    handleMenuClick={onFileActionClick}
                />
            )}
             {hasShare
                && (
                    <button
                        type="button"
                        className={classNames(
                            commonStyle['blue-half'],
                            styles['share-grid-area'],
                            styles['send-icon-color'],
                            styles['linked-button'],
                            styles['icon-button'],
                        )}
                        onClick={() => handleMenuClick('share', fileId)}
                        data-clickable
                    >
                        <SendOutlined data-testid="myFiles_WhoHasAccess" />
                    </button>
                )}
            {hasCopyLink
                && (
                    <ColoredTooltip title={t('filesTable.contextMenu.copyLink')}>
                        <button
                            type="button"
                            aria-label={t('filesTable.contextMenu.copyLink')}
                            className={classNames(
                                commonStyle['blue-half'],
                                styles['share-grid-area'],
                                styles['send-icon-color'],
                                styles['linked-button'],
                                styles['icon-button'],
                            )}
                            onClick={() => handleMenuClick('copy_link', fileId)}
                            data-clickable
                            data-testid="myFiles_CopyLink"
                        >
                            <LinkOutlined />
                        </button>
                    </ColoredTooltip>
                )}
            {moreOptions.length > 0 && (
                <FileActionsDropdown
                    buttonClass={styles['more-grid-area']}
                    buttonTitle="more"
                    options={moreOptions}
                    handleMenuClick={onFileActionClick}
                    openIcon={openIcon}
                />
            )}
        </div>
    );
});

export default Actions;

export { items } from '../../../FileAction/menuItems';
